/**
 * Logique PURE des événements Live School affichés dans les onglets des pages
 * d'actus. Miroir client de `getQuizState` : classe chaque défi en `upcoming`
 * (à venir) / `live` (en cours, tolérance incluse) / `finished` (terminé) à
 * partir de l'heure serveur renvoyée par l'edge fn `live-events`. Aucun accès à
 * `Date.now()` — l'appelant passe une horloge explicite (testable).
 */

import { getQuizState } from '@jeraisonne/db/live';
import { CARD_RARITY_STARS, type CardRarity } from '@jeraisonne/db/cards';

/** Métadonnées d'affichage d'un défi (jamais de payload chiffré / correction). */
export interface LiveEvent {
  id: string;
  level: string;
  subject: string | null;
  starts_at: string;
  step_duration_ms: number;
  step_count: number;
  tolerance_ms: number;
  status: string;
  title: string | null;
  campaign_id: string | null;
  /** Live School du dimanche (0329) : programme source (null = défi historique). */
  programme_id?: string | null;
  /** Fenêtre « prépare-toi » avant la 1re question (ms). */
  reading_ms?: number;
  scoring?: 'hora' | 'points';
  /** Spécial des vacances (Halloween, Noël…, migration 0332). */
  special?: boolean;
  /** Lot : SKILLCARD gagnée par le top N (choisie par l'admin, sinon une carte du pack du moment). */
  prize?: { card_id: string; top_n: number } | null;
}

/** Titre + rareté publiés des SKILLCARDS lots (clé = card_id). */
export type LivePrizeCards = Record<
  string,
  {
    title: string;
    rarity: string;
    color_theme?: string | null;
    creature?: unknown;
    levels?: string[];
  }
>;

/** Podium d'un live passé (gagnants du lot). */
export interface LivePrizeWinner {
  rank: number;
  student_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

/** Cover d'une campagne référencée par un défi (hero de l'onglet Live School). */
export interface LiveCampaignCover {
  id: string;
  title: string;
  subtitle: string | null;
  cover_url: string | null;
}

export type LiveEventPhase = 'upcoming' | 'live' | 'finished';

function ms(iso: string): number {
  return new Date(iso).getTime();
}

/** Phase d'affichage d'un défi à un instant serveur donné. Déterministe. */
export function liveEventPhase(event: LiveEvent, serverTime: number): LiveEventPhase {
  const startsAt = ms(event.starts_at);
  // Les questions démarrent après la fenêtre « prépare-toi » (reading_ms).
  const questionsAt = startsAt + (event.reading_ms ?? 0);
  if (serverTime < startsAt) return 'upcoming';
  const state = getQuizState({
    serverTime,
    startsAt: questionsAt,
    stepDurationMs: event.step_duration_ms,
    stepCount: event.step_count,
  });
  if (state.status === 'WAITING' || state.status === 'LIVE') return 'live';
  // FINISHED : encore « en cours » pendant la fenêtre de tolérance (dernière réponse).
  const endMs = questionsAt + event.step_count * event.step_duration_ms;
  if (serverTime < endMs + event.tolerance_ms) return 'live';
  return 'finished';
}

export interface PartitionedLiveEvents {
  /** En cours (fenêtre de tolérance incluse), tri du plus ancien au plus récent. */
  live: LiveEvent[];
  /** À venir, tri chronologique croissant (le prochain d'abord). */
  upcoming: LiveEvent[];
  /** Terminés, tri décroissant (le plus récent d'abord). */
  finished: LiveEvent[];
}

/**
 * Répartit et trie une liste de défis (dédupliquée par id) en trois seaux
 * d'affichage. Les entrées `upcoming`/`past` de l'edge fn peuvent être fusionnées
 * puis re-classées ici (un défi « past » par `starts_at` peut être encore live).
 */
export function partitionLiveEvents(
  events: LiveEvent[],
  serverTime: number,
): PartitionedLiveEvents {
  const seen = new Set<string>();
  const live: LiveEvent[] = [];
  const upcoming: LiveEvent[] = [];
  const finished: LiveEvent[] = [];
  for (const e of events) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    const phase = liveEventPhase(e, serverTime);
    if (phase === 'live') live.push(e);
    else if (phase === 'upcoming') upcoming.push(e);
    else finished.push(e);
  }
  const asc = (a: LiveEvent, b: LiveEvent) => ms(a.starts_at) - ms(b.starts_at);
  const desc = (a: LiveEvent, b: LiveEvent) => ms(b.starts_at) - ms(a.starts_at);
  live.sort(asc);
  upcoming.sort(asc);
  finished.sort(desc);
  return { live, upcoming, finished };
}

/** Défis déjà démarrés (en cours + terminés), plus récent d'abord — candidats classement. */
export function rankableChallenges(events: PartitionedLiveEvents): LiveEvent[] {
  return [...events.live, ...events.finished].sort((a, b) => ms(b.starts_at) - ms(a.starts_at));
}

/**
 * Campagne à afficher en hero : celle (avec cover) du défi le plus proche
 * (en cours → à venir → passé). Repli sur la 1re campagne avec cover. `null`
 * si aucune cover disponible.
 */
export function pickHeroCampaign(
  buckets: PartitionedLiveEvents,
  campaigns: LiveCampaignCover[],
): LiveCampaignCover | null {
  if (campaigns.length === 0) return null;
  const byId = new Map(campaigns.map((c) => [c.id, c]));
  for (const e of [...buckets.live, ...buckets.upcoming, ...buckets.finished]) {
    if (!e.campaign_id) continue;
    const c = byId.get(e.campaign_id);
    if (c?.cover_url) return c;
  }
  return campaigns.find((c) => c.cover_url) ?? null;
}

export function liveEventPhaseLabel(phase: LiveEventPhase): string {
  return phase === 'live' ? 'En cours' : phase === 'upcoming' ? 'À venir' : 'Terminé';
}

/** Date + heure FR longues (cartes d'événement). */
export function formatLiveEventDateTime(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Date FR courte (pills du sélecteur de classement). */
export function formatLiveEventShortDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' }).format(new Date(iso));
}

/** « 10 h 15 » (heure de Paris) — horaire d'un live du dimanche. */
export function formatLiveEventTime(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

/** « dimanche 11 octobre » (heure de Paris). */
export function formatLiveEventDay(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

/** Regroupe des lives par jour (heure de Paris), ordre d'entrée conservé. */
export function groupLiveEventsByDay(events: LiveEvent[]): { day: string; events: LiveEvent[] }[] {
  const groups: { day: string; events: LiveEvent[] }[] = [];
  for (const e of events) {
    const day = formatLiveEventDay(e.starts_at);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.events.push(e);
    else groups.push({ day, events: [e] });
  }
  return groups;
}

/** « Live School · Mathématiques 3e » → « Mathématiques 3e ». */
export function liveEventProgrammeLabel(event: Pick<LiveEvent, 'title'>): string {
  const t = event.title ?? 'Défi National';
  return t.replace(/^Live School\s*·\s*/, '');
}

/** Durée totale d'un live, arrondie à la minute supérieure. */
export function liveEventDurationMin(
  event: Pick<LiveEvent, 'step_count' | 'step_duration_ms' | 'reading_ms'>,
): number {
  return Math.ceil(((event.reading_ms ?? 0) + event.step_count * event.step_duration_ms) / 60_000);
}

/** Étoiles de rareté d'une SKILLCARD lot (« ★★★ »), 1 étoile si rareté inconnue. */
export function livePrizeStars(rarity: string | null | undefined): string {
  return '★'.repeat(CARD_RARITY_STARS[rarity as CardRarity] ?? 1);
}
